import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';  // Updated to use AuthContext
import { useLoading } from '../contexts/LoadingContext';  // Use LoadingContext
import { db } from '../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { TextField, Typography, Button, Snackbar, Paper, CircularProgress } from '@mui/material';

const OrganizationSettingsPage = () => {
  const { currentUser, organizationId } = useAuth(); // Using organizationId from context
  const { isLoading, setLoading } = useLoading(); // Use LoadingContext for loading state management
  const [organization, setOrganization] = useState({});
  const [initialOrganization, setInitialOrganization] = useState({}); // Track initial organization data
  const [isModified, setIsModified] = useState(false); // Track if any changes are made
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [saving, setSaving] = useState(false); // Local state for saving process

  // Fetch organization data from Firestore
  useEffect(() => {
    if (currentUser && organizationId) {
      setLoading(true, 'OrganizationSettingsFetch');
      const orgRef = doc(db, 'organizations', organizationId); // Using organizationId from context

      getDoc(orgRef)
        .then((orgSnap) => {
          if (orgSnap.exists()) {
            const orgData = orgSnap.data();
            setOrganization(orgData); // Set the current organization data
            setInitialOrganization(orgData); // Set the initial organization data
          } else {
            console.error("Organization not found in Firestore.");
            setToastMessage("Organization not found");
          }
        })
        .catch((error) => {
          console.error("Error fetching organization:", error);
          setToastMessage("Error fetching organization data");
        })
        .finally(() => {
          setLoading(false, 'OrganizationSettingsFetch');
        });
    }
  }, [currentUser, organizationId, setLoading]);

  // Check if changes are made to enable/disable save button
  useEffect(() => {
    const isOrgNameChanged = initialOrganization?.name !== organization?.name;
    setIsModified(isOrgNameChanged); // Enable save button only if org name has been modified
  }, [organization, initialOrganization]);

  // Handle organization save
  const handleSave = async () => {
    if (!saving && organization && organizationId) {
      setSaving(true); // Only set saving for this process
      const orgRef = doc(db, 'organizations', organizationId); // Using organizationId from context

      try {
        await updateDoc(orgRef, organization);
        setInitialOrganization(organization); // Update the initial organization after saving
        setToastMessage("Organization details updated successfully");
      } catch (error) {
        console.error("Error updating organization:", error);
        setToastMessage("Error updating organization");
      } finally {
        setSaving(false); // Reset saving state after process
        setToastOpen(true);  // Show toast after saving
      }
    }
  };

  // Handle changes in organization fields
  const handleChange = (e) => {
    setOrganization((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // Close toast notification
  const handleCloseToast = () => {
    setToastOpen(false);
  };

  return (
    <Paper sx={{ width: '100%', p: 3, minHeight: '400px' }}>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
        My Organization
      </Typography>
      <TextField
        label="Organization Name"
        variant="outlined"
        name="name"
        value={organization?.name || ''}
        onChange={handleChange}
        fullWidth
        sx={{ mb: 2 }}
      />
      <Button
        onClick={handleSave}
        disabled={!isModified || saving}  // Disable button if not modified or saving
        color="primary"
        variant="contained"
        sx={{ position: 'relative' }}
      >
        {saving ? <CircularProgress size={24} sx={{ position: 'absolute' }} /> : 'Save'}
      </Button>
      <Snackbar
        open={toastOpen && !!toastMessage}  // Only open if there is a message
        autoHideDuration={6000}
        onClose={handleCloseToast}
        message={toastMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Paper>
  );
};

export default OrganizationSettingsPage;
